// src/components/dashboards/SalaryStructures.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { financeAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

/* ─── helpers ────────────────────────────────────────────────────────────── */
const fmt = (n) =>
  n !== null && n !== undefined
    ? `₹${parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const FACULTY_SUBROLES = [
  { value: 'SENIOR_RESIDENT',     label: 'Senior Resident' },
  { value: 'ASSISTANT_PROFESSOR', label: 'Assistant Professor' },
  { value: 'ASSOCIATE_PROFESSOR', label: 'Associate Professor' },
  { value: 'PROFESSOR',           label: 'Professor' },
  { value: 'HEAD_OF_DEPARTMENT',  label: 'Head of Department' },
  { value: 'PROFESSOR_HOD',       label: 'Professor + HOD' },
  { value: 'DEAN',                label: 'Dean (Faculty)' },
];

const OTHER_ROLES = [
  { value: 'ADMIN',                 label: 'Admin' },
  { value: 'FINANCE',               label: 'Finance' },
  { value: 'COE',                   label: 'Controller of Examinations' },
  { value: 'HOSTEL_ADMIN',          label: 'Hostel Admin' },
  { value: 'LIBRARY_STAFF',         label: 'Library Staff' },
  { value: 'TRANSPORT_MANAGER',     label: 'Transport Manager' },
  { value: 'recepsonist',           label: 'Receptionist' },
  { value: 'LAB_TECH_MICROBIOLOGY', label: 'Lab Tech – Microbiology' },
  { value: 'LAB_TECH_PATHOLOGY',    label: 'Lab Tech – Pathology' },
  { value: 'LAB_TECH_BIOCHEMISTRY', label: 'Lab Tech – Biochemistry' },
  { value: 'BILLING_MEMBER',        label: 'Billing Member' },
  { value: 'BILLING_MANAGER',       label: 'Billing Manager' },
  { value: 'SAMPLE_COLLECTOR',      label: 'Sample Collector' },
  { value: 'REPORT_GENERATION',     label: 'Report Generation' },
  { value: 'GRIEVANCE_OFFICER',     label: 'Grievance Officer' },
  { value: 'PRINCIPAL',             label: 'Principal' },
  { value: 'DEAN',                  label: 'Dean (Admin)' },
  { value: 'PAPER_EVALUATOR',       label: 'Paper Evaluator' },
  { value: 'STORE_MANAGER',         label: 'Store Manager' },
  { value: 'INTERN',                label: 'Intern' },
];

const EMPTY_FORM = {
  type: 'role',          // 'faculty_subrole' | 'role'
  faculty_subrole: '',
  role: '',
  monthly_salary: '',
  is_active: true,
};

/* ─── tiny inline styles (no new CSS file needed) ───────────────────────── */
const S = {
  wrap: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    color: '#1a1d2b',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 24,
  },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  sub: { fontSize: 13, color: '#6b7280', marginTop: 2 },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
    color: '#fff', fontWeight: 600, fontSize: 14, boxShadow: '0 2px 8px #7c3aed44',
    transition: 'opacity .2s',
  },
  section: {
    background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px #0001',
    border: '1px solid #e5e7eb', marginBottom: 24, overflow: 'hidden',
  },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 20px', background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  sectionIcon: {
    width: 34, height: 34, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16,
  },
  sectionTitle: { fontSize: 15, fontWeight: 600, margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: .5,
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
  },
  td: { padding: '12px 16px', fontSize: 14, borderBottom: '1px solid #f3f4f6' },
  badge: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    background: active ? '#dcfce7' : '#f3f4f6',
    color: active ? '#16a34a' : '#9ca3af',
  }),
  editBtn: {
    padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb',
    background: '#fff', cursor: 'pointer', fontSize: 13, marginRight: 6,
    color: '#374151', fontWeight: 500,
  },
  delBtn: {
    padding: '5px 12px', borderRadius: 7, border: '1px solid #fecaca',
    background: '#fff', cursor: 'pointer', fontSize: 13,
    color: '#ef4444', fontWeight: 500,
  },
  salary: { fontWeight: 700, color: '#16a34a', fontFamily: 'monospace', fontSize: 15 },
  /* modal */
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
    padding: 28, boxShadow: '0 20px 60px #0004',
  },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#1a1d2b' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    outline: 'none', marginBottom: 14,
  },
  select: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    marginBottom: 14, background: '#fff',
  },
  row: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb',
    background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14,
  },
  saveBtn: {
    padding: '9px 24px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
    color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14,
  },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
  },
  toggle: { width: 38, height: 22, cursor: 'pointer' },
  notice: {
    padding: '12px 16px', borderRadius: 10, background: '#fffbeb',
    border: '1px solid #fde68a', color: '#92400e', fontSize: 13, marginBottom: 16,
  },
  emptyRow: { textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '28px 0' },
};

/* ─── component ─────────────────────────────────────────────────────────── */
export default function SalaryStructures() {
  const [structures, setStructures]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);   // null = create, obj = edit
  const [form, setForm]               = useState(EMPTY_FORM);
  const [filter, setFilter]           = useState('all'); // 'all' | 'faculty' | 'staff'

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await financeAPI.getAllSalaryStructures();
      setStructures(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError('Failed to load salary structures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      type:            s.faculty_subrole ? 'faculty_subrole' : 'role',
      faculty_subrole: s.faculty_subrole || '',
      role:            s.role || '',
      monthly_salary:  s.monthly_salary,
      is_active:       s.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (s) => {
    if (!await confirmDialog(`Delete salary structure for "${s.role_display}"?`)) return;
    try {
      await financeAPI.deleteSalaryStructure(s.id);
      flash('Deleted successfully.');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed.');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      monthly_salary: parseFloat(form.monthly_salary),
      is_active:      form.is_active,
      faculty_subrole: form.type === 'faculty_subrole' ? form.faculty_subrole : null,
      role:            form.type === 'role'            ? form.role            : null,
    };
    try {
      setSaving(true);
      if (editing) {
        await financeAPI.updateSalaryStructure(editing.id, payload);
        flash('Salary structure updated.');
      } else {
        await financeAPI.createSalaryStructure(payload);
        flash('Salary structure created.');
      }
      setShowModal(false);
      load();
    } catch (e) {
      const detail = e.response?.data;
      const msg = typeof detail === 'string'
        ? detail
        : JSON.stringify(detail) || 'Save failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3500);
  };

  // ── split into two groups ────────────────────────────────────────────────
  const faculty = structures.filter(s => s.faculty_subrole);
  const staff   = structures.filter(s => s.role);

  const visible =
    filter === 'faculty' ? { Faculty: faculty } :
    filter === 'staff'   ? { Staff: staff } :
    { 'Faculty Subroles': faculty, 'Non-Faculty Staff': staff };

  const groupColors = {
    'Faculty Subroles':   { bg: '#ede9fe', icon: '🎓' },
    'Non-Faculty Staff':  { bg: '#dbeafe', icon: '🏥' },
    'Faculty':            { bg: '#ede9fe', icon: '🎓' },
    'Staff':              { bg: '#dbeafe', icon: '🏥' },
  };

  return (
    <div style={S.wrap}>
      {/* ── page header ── */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Salary Structures</h2>
          <p style={S.sub}>
            Manage monthly salary for every role. Changes apply to all future payroll calculations.
          </p>
        </div>
        <button style={S.addBtn} onClick={openCreate}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Structure
        </button>
      </div>

      {/* ── alerts ── */}
      {error && (
        <div style={{ ...S.notice, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c', marginBottom: 16 }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
      )}
      {success && (
        <div style={{ ...S.notice, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534', marginBottom: 16 }}>
          ✓ {success}
        </div>
      )}

      {/* ── filter tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['all', 'All'], ['faculty', '🎓 Faculty'], ['staff', '🏥 Staff']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all .15s',
              background: filter === val ? '#4f46e5' : '#f3f4f6',
              color: filter === val ? '#fff' : '#374151',
              border: filter === val ? '1px solid #4f46e5' : '1px solid #e5e7eb',
            }}
          >{label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>
          {structures.length} structure{structures.length !== 1 ? 's' : ''} total
        </span>
      </div>

      {/* ── tables by group ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading…</div>
      ) : (
        Object.entries(visible).map(([groupName, rows]) => (
          <div key={groupName} style={S.section}>
            <div style={S.sectionHead}>
              <div style={{ ...S.sectionIcon, background: groupColors[groupName]?.bg || '#f3f4f6' }}>
                {groupColors[groupName]?.icon || '💼'}
              </div>
              <div>
                <p style={S.sectionTitle}>{groupName}</p>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                  {rows.length} entr{rows.length !== 1 ? 'ies' : 'y'}
                </p>
              </div>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Designation / Role</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Monthly Salary</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={4} style={S.emptyRow}>No entries in this group</td></tr>
                ) : rows.map(s => (
                  <tr key={s.id} style={{ transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={S.td}>
                      <span style={{ fontWeight: 600 }}>{s.role_display}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>
                        {s.faculty_subrole ? 'Faculty' : s.role}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <span style={S.salary}>{fmt(s.monthly_salary)}</span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <span style={S.badge(s.is_active)}>
                        {s.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.editBtn} onClick={() => openEdit(s)}>Edit</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(s)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {/* ── modal ── */}
      {showModal && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={S.modalTitle}>{editing ? 'Edit Salary Structure' : 'New Salary Structure'}</h3>

            <form onSubmit={handleSave}>
              {/* type toggle */}
              {!editing && (
                <>
                  <label style={S.label}>Structure Type</label>
                  <select
                    style={S.select}
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value, faculty_subrole: '', role: '' }))}
                  >
                    <option value="faculty_subrole">Faculty Subrole</option>
                    <option value="role">Non-Faculty Role</option>
                  </select>
                </>
              )}

              {/* role selector */}
              {form.type === 'faculty_subrole' && (
                <>
                  <label style={S.label}>Faculty Subrole</label>
                  <select
                    style={S.select}
                    value={form.faculty_subrole}
                    onChange={e => setForm(f => ({ ...f, faculty_subrole: e.target.value }))}
                    required
                    disabled={!!editing}
                  >
                    <option value="">— Select Subrole —</option>
                    {FACULTY_SUBROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </>
              )}

              {form.type === 'role' && (
                <>
                  <label style={S.label}>Staff Role</label>
                  <select
                    style={S.select}
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    required
                    disabled={!!editing}
                  >
                    <option value="">— Select Role —</option>
                    {OTHER_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </>
              )}

              <label style={S.label}>Monthly Salary (₹)</label>
              <input
                style={S.input}
                type="number"
                min="0"
                step="100"
                required
                value={form.monthly_salary}
                onChange={e => setForm(f => ({ ...f, monthly_salary: e.target.value }))}
                placeholder="e.g. 45000"
              />

              <div style={S.toggleRow}>
                <input
                  id="is_active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                />
                <label htmlFor="is_active" style={{ ...S.label, margin: 0, cursor: 'pointer' }}>
                  Active (included in payroll)
                </label>
              </div>

              <div style={S.row}>
                <button type="button" style={S.cancelBtn} onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={S.saveBtn} disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}